import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { BookOpen, HardDrive, Bug, AlertTriangle } from 'lucide-react'
import { cn } from '../../lib/utils'

const ONBOARDING_KEY = 'onboarding-complete'

function isOnboardingComplete(): boolean {
  try {
    return localStorage.getItem(ONBOARDING_KEY) === 'true'
  } catch {
    return false
  }
}

function markOnboardingComplete(): void {
  try {
    localStorage.setItem(ONBOARDING_KEY, 'true')
  } catch {
    // Storage full or unavailable — let them through anyway
  }
}

export const WelcomeModal = memo(function WelcomeModal() {
  const [isOpen, setIsOpen] = useState(() => !isOnboardingComplete())
  const [acknowledged, setAcknowledged] = useState(false)
  const checkboxRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!isOpen) return

    checkboxRef.current?.focus()

    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = originalOverflow
    }
  }, [isOpen])

  const handleContinue = useCallback(() => {
    if (!acknowledged) return
    markOnboardingComplete()
    setIsOpen(false)
  }, [acknowledged])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[200] overscroll-contain bg-black/60 px-4 py-6">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="welcome-title"
        className={cn(
          'mx-auto mt-8 w-full max-w-lg rounded-xl border border-border bg-card shadow-2xl',
          'max-h-[90vh] overflow-y-auto'
        )}
      >
        <div className="px-6 pt-6 pb-2">
          <h2
            id="welcome-title"
            className="text-xl font-bold text-foreground"
          >
            Welcome to LearnPrediction.Markets
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            An interactive platform that teaches you how prediction markets work
            — from order books and matching engines to trading strategies —
            through hands-on simulations.
          </p>
        </div>

        <div className="space-y-3 px-6 py-4">
          <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/40 p-3">
            <BookOpen className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
            <div>
              <p className="text-sm font-medium text-foreground">Guided lessons & sandbox</p>
              <p className="text-xs text-muted-foreground">
                Work through lessons at your own pace, then experiment freely in
                the sandbox with simulated markets.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/40 p-3">
            <HardDrive className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
            <div>
              <p className="text-sm font-medium text-foreground">No account needed</p>
              <p className="text-xs text-muted-foreground">
                Your progress is saved in your browser's local storage. It stays
                on this device and isn't synced across devices.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/40 p-3">
            <Bug className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" aria-hidden="true" />
            <div>
              <p className="text-sm font-medium text-foreground">Still in development</p>
              <p className="text-xs text-muted-foreground">
                This platform is a work in progress. If you run into a bug,
                please report it using the <span className="font-medium">bug button</span> in the top bar.
              </p>
            </div>
          </div>
        </div>

        <div className="border-t border-border px-6 py-4">
          <label className="flex cursor-pointer items-start gap-3">
            <input
              ref={checkboxRef}
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-border text-primary focus-visible:ring-2 focus-visible:ring-primary"
            />
            <span className="text-sm text-foreground">
              <AlertTriangle className="mr-1 inline-block h-4 w-4 text-amber-500" aria-hidden="true" />
              I understand this platform is <span className="font-semibold">for educational purposes only</span> and
              does not constitute financial advice, trading advice, or investment
              recommendations of any kind.
            </span>
          </label>

          <button
            type="button"
            onClick={handleContinue}
            disabled={!acknowledged}
            className={cn(
              'mt-4 w-full rounded-md px-4 py-2.5 text-sm font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
              acknowledged
                ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                : 'cursor-not-allowed bg-muted text-muted-foreground'
            )}
          >
            Get Started
          </button>
        </div>
      </div>
    </div>
  )
})
